/**
 * Holding something on the phone's calendar to move it — Aaron, Oct 27, note
 * 1.1.4 ("replicate the outlook feature for when i click and hold on an event
 * it allows me to drag it around into another time slot") and answers 6 and
 * 8, on Day and 3 days:
 *
 * - **touch and hold** a meeting, a site visit, a time block or a timed task
 *   the viewer may move (the desktop's rule, `editable`) and it lifts — a
 *   little larger, with a shadow — and follows the finger in 15-minute steps,
 *   across the days of 3 days and the people of Day side by side, the hours
 *   scrolling under it at their top and bottom; let go, and it is saved as
 *   the desktop saves a drag (`moves.ts`), a repeat asking **Just this one ·
 *   Every one** first. A quick tap still opens it; a finger that moves first
 *   is a scroll;
 * - **edit mode** — held and lifted without moving it, or right after a touch
 *   and hold made a new hour: two round handles, top and bottom, change its
 *   start and its end in 15-minute steps (answer 6: "dragging the length when
 *   it's in edit mode would be great - but default to 1 hour is perfect"),
 *   the block itself drags, and a bar at the foot says **Cancel** and **Done**
 *   — **Next** for a new one, which opens New meeting at that time and length.
 *
 * This module is the state and what stands over the calendar; the gestures
 * are `phone-day.tsx`'s and the arithmetic `phone-time.ts`'s.
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { ApiError, clockRange, dayComma, zonedInstant, type CalendarDrive, type CalendarItem } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { Glyph } from '../glyphs.js'
import { BackLayer } from '../use-activity-history.js'
import { activityRequest } from '../api.js'
import { openNewRequest } from '../../requests/open-request.js'
import { StopBlock, TaskChip, TimeBlock } from './blocks.js'
import { BLOCK_GAP, span } from './look.js'
import { asksWhich, dropRefusal } from './drag.js'
import { writeMove } from './moves.js'
import { handleBoxes, hasLength, liftPlan, type LiftPlace } from './phone-time.js'
import { TellPrompt, WhichSheet, whichLines } from './actions.js'
import { messageAbout, readStop, type StopCardAnswer } from './overlays.js'
import type { PhoneSlot } from './phone-day.js'

/**
 * What a finger has lifted, or the new hour in edit mode, and where it is
 * going. It is drawn from its `home` column — the one it was lifted in — the
 * whole time, moved across to the column it is over, so the element under
 * the finger is never taken out of the page mid-drag (`phone-finger.ts`).
 */
export interface PhoneLift {
  /** The thing lifted; null for a new hour (Next opens New meeting for it). */
  item: CalendarItem | null
  /** The column it was lifted in: a day, or a person on Day side by side. */
  home: string
  from: LiftPlace
  to: LiftPlace
  start: number
  end: number
  /**
   * `drag`: following a finger; `edit`: handles and the bar; `held`: where it
   * was dropped, until its repeat is answered; `saving`: there until the
   * calendar's next answer draws it there for real.
   */
  mode: 'drag' | 'edit' | 'held' | 'saving'
}

/** *Thu, Oct 22 · 1:30–2:30 PM*. */
export const liftWords = (lift: Pick<PhoneLift, 'to' | 'start' | 'end'>): string =>
  `${dayComma(lift.to.date)} · ${clockRange(lift.start, lift.end)}`

/** What a failed write says, as the server worded it. */
const sentenceOf = (cause: unknown, fallback: string) =>
  cause instanceof ApiError && cause.message ? cause.message : fallback

// ---------------------------------------------------------------------------
// Drawn in the hours
// ---------------------------------------------------------------------------

/** Across to the column `columns` over from home: each column is its body's width and a 1 px rule. */
const across = (columns: number, inset: number) => `calc(${inset}px + ${columns} * (100% + 1px))`

/**
 * The lifted thing where it is going — the block the desktop draws, a little
 * larger with a shadow while a finger carries it, ringed in edit mode — and in
 * edit mode its two handles. A new hour is the ghost, solid-edged.
 */
export function LiftedBlock({
  lift, columns, scale, narrow, edge, drive = null, title, onPress,
}: {
  lift: PhoneLift
  /** A stop's drive where it is going, as the server re-worked it (Board 6 B). */
  drive?: CalendarDrive | null
  /** How many columns over from home it is (negative: to the left). */
  columns: number
  scale: number
  narrow: boolean
  edge?: string
  /** A new hour's words: *New meeting*, *New meeting for Luis*. */
  title: string
  /** Edit mode: a press on the block (`move`) or on a handle. */
  onPress: (part: 'move' | 'start' | 'end', event: React.PointerEvent<HTMLElement>) => void
}) {
  const editing = lift.mode === 'edit'
  const item = lift.item
  const top = lift.start * scale
  const height = (lift.end - lift.start) * scale - BLOCK_GAP
  const style: CSSProperties = {
    top, left: across(columns, 3), width: 'calc(100% - 6px)', zIndex: 9,
    /* A little larger while a finger carries it (Outlook's lift); edit mode and after, its own size. */
    transform: lift.mode === 'drag' ? 'scale(1.03)' : undefined, transformOrigin: 'center',
  }
  const press = editing ? (event: React.PointerEvent<HTMLElement>) => onPress('move', event) : undefined
  const look = editing ? 'edit' : lift.mode === 'drag' ? 'drag' : 'held'
  let block
  if (!item) {
    block = (
      <div data-new-ghost="" data-lifted={look} onPointerDown={press} role={editing ? 'group' : undefined}
        aria-label={editing ? `${title}, ${liftWords(lift)}` : undefined}
        className={cn('absolute overflow-hidden rounded-[7px] border-2 border-accent bg-accent-soft py-1 pl-2.5 pr-[7px] leading-[normal]',
          editing ? 'touch-none shadow-popover' : 'pointer-events-none border-dashed')}
        style={{ ...style, height }}>
        <span className="tnum block whitespace-nowrap text-[10.5px] font-semibold text-text-muted">{span(lift.start, lift.end)}</span>
        <b className="mt-px block truncate text-[12px] leading-[1.25]">{title}</b>
      </div>
    )
  } else {
    const moved = { ...item, date: lift.to.date, start: lift.start, end: lift.end }
    if (item.kind === 'task') {
      block = <TaskChip item={moved} lifted={look} style={{ ...style, right: undefined }} onPointerDown={press} />
    } else if (item.kind === 'block') {
      block = <TimeBlock item={moved} scale={scale} narrow={narrow} edge={edge} lifted={look} style={style} onPointerDown={press} resizable={false} />
    } else {
      block = <StopBlock item={moved} drive={drive} scale={scale} narrow={narrow} edge={edge} showFaces={!narrow} lifted={look} style={style}
        onPointerDown={press} resizable={false} />
    }
  }
  const withHandles = editing && (!item || hasLength(item))
  const boxes = handleBoxes(top, height)
  return (
    <>
      {block}
      {withHandles && (['start', 'end'] as const).map((part) => {
        const box = boxes[part]
        /* The dot sits on the edge: the box reaches out from it, and in no further than half the block. */
        const edgeAt = part === 'start' ? top - box.top : top + height - box.top
        return (
          <button key={part} type="button" data-handle={part}
            aria-label={part === 'start' ? 'Change when it starts' : 'Change when it ends'}
            onPointerDown={(event) => onPress(part, event)}
            className="absolute flex touch-none justify-center"
            style={{ top: box.top, height: box.height, width: 44, left: `calc(${across(columns, 0)} + 50% - 22px)`, zIndex: 10 }}>
            <i aria-hidden className="absolute block h-3 w-3 rounded-full border-2 border-accent bg-surface shadow-popover"
              style={{ top: edgeAt - 6 }} />
          </button>
        )
      })}
    </>
  )
}

// ---------------------------------------------------------------------------
// The state, and what stands over the calendar
// ---------------------------------------------------------------------------

/**
 * The bar at the foot in edit mode: **Cancel**, when it is and what, and
 * **Done** — **Next** for a new hour. 44 px each, above the tab bar.
 */
export function EditBar({ lift, title, onCancel, onDone }: {
  lift: PhoneLift; title: string; onCancel: () => void; onDone: () => void
}) {
  const fresh = !lift.item
  return (
    <div role="toolbar" aria-label={`${fresh ? title : `Editing ${lift.item!.title}`}`} data-lift-bar=""
      className="fixed inset-x-3 z-[60] flex items-center gap-2 rounded-[14px] border border-line bg-surface p-1.5 leading-[normal] shadow-popover"
      style={{ bottom: 'calc(10px + var(--activity-foot, 0px))' }}>
      <button type="button" onClick={onCancel}
        className="inline-flex h-11 shrink-0 items-center rounded-lg border border-line bg-surface px-4 text-[14px] font-semibold text-text">
        Cancel
      </button>
      <span className="flex min-w-0 flex-1 flex-col items-center text-center">
        {/* The time first — it is what the handles change — then the day and what it is. */}
        <b className="tnum max-w-full truncate text-[13.5px]">{clockRange(lift.start, lift.end)}</b>
        <span className="max-w-full truncate text-[12px] text-text-muted">{dayComma(lift.to.date)} · {fresh ? title : lift.item!.title}</span>
      </span>
      <button type="button" onClick={onDone}
        className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg border border-accent bg-accent px-4 text-[14px] font-semibold text-white">
        {fresh ? <>Next<Glyph name="chevronRight" size={16} /></> : <><Glyph name="check" size={16} />Done</>}
      </button>
    </div>
  )
}

interface Asking {
  item: CalendarItem
  verb: 'move' | 'resize'
  to: { date: string; start: number; minutes: number }
  onPick: (which: 'one' | 'all') => void
  onCancel: () => void
}

/** *Renee Castillo Residence runs into Fox Hollow at 12:30. Keep it here?* — and how to put it back. */
interface RunsInto { item: CalendarItem; words: string; undo: () => Promise<unknown> }

const isStop = (item: CalendarItem | null): item is CalendarItem => item?.kind === 'appointment' || item?.kind === 'meeting'

/**
 * The stop a finger carries asks the server for its drive and the next one's
 * while it moves, as the desktop's drag does (§6.5, Board 6 B) — only for a
 * move, because a new length is not something the drive estimate is told.
 */
function routeKeyOf(lift: PhoneLift | null): string | null {
  if (!lift || !isStop(lift.item) || lift.mode === 'saving') return null
  if (lift.end - lift.start !== lift.item.end - lift.item.start) return null
  return `${lift.item.key}|${lift.to.personId}|${lift.to.date}|${lift.start}`
}

/**
 * The phone calendar's half of a lift: what is lifted, writing it where it
 * was put (the desktop's moves, its refusals, its repeat question and its
 * *Keep it here?*), and telling the customer after (*Let Renee know?*, note
 * 1.8).
 */
export function usePhoneLift({ answerKey, where, zone, canSend, reload, onNext, titleOf }: {
  /** The calendar's latest answer: a saved lift stays drawn until the next one arrives. */
  answerKey: unknown
  /** The view and its days: when they change, whatever was lifted goes back where it was. */
  where: string
  zone: string
  canSend: boolean
  reload: () => void
  /** Next on a new hour: New meeting at that time and length. */
  onNext: (slot: PhoneSlot) => void
  /** A new hour's words, by whose it is. */
  titleOf: (personId: string) => string
}) {
  const [lift, setLift] = useState<PhoneLift | null>(null)
  const [asking, setAsking] = useState<Asking | null>(null)
  const [into, setInto] = useState<RunsInto | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [tell, setTell] = useState<StopCardAnswer | null>(null)
  const [route, setRoute] = useState<{ key: string; drives: CalendarDrive[]; runsInto: string | null } | null>(null)

  /* Saved: the calendar's next answer draws it there for real. */
  useEffect(() => { setLift((was) => (was?.mode === 'saving' ? null : was)) }, [answerKey])
  /* Another day (the strip, a swipe, the view): whatever was up goes back where it was. */
  useEffect(() => {
    setLift((was) => (was && was.mode !== 'saving' ? null : was))
    setAsking(null)
  }, [where])
  useEffect(() => {
    if (!notice) return undefined
    const timer = window.setTimeout(() => setNotice(null), 4500)
    return () => window.clearTimeout(timer)
  }, [notice])

  /* Its drive and the next stop's, re-worked as it moves (the server's, 150 ms after it settles). */
  const routeKey = routeKeyOf(lift)
  const routeAsk = useRef<PhoneLift | null>(null)
  routeAsk.current = lift
  useEffect(() => {
    const now = routeAsk.current
    if (!routeKey || !now?.item) return undefined
    const item = now.item
    const timer = window.setTimeout(() => {
      const at = zonedInstant(now.to.date, now.start, zone).toISOString()
      const elsewhere = now.to.date !== now.from.date || now.to.personId !== now.from.personId
        ? `&fromDate=${now.from.date}&fromPerson=${now.from.personId}` : ''
      void activityRequest<{ drives: CalendarDrive[]; runsInto: string | null }>(
        `/activity/drives?people=${now.to.personId}&date=${now.to.date}&moving=${encodeURIComponent(item.key)}&to=${encodeURIComponent(at)}${elsewhere}`)
        .then((value) => setRoute({ key: routeKey, drives: value.drives, runsInto: value.runsInto }))
        .catch(() => {})
    }, 150)
    return () => window.clearTimeout(timer)
  }, [routeKey, zone])
  const drives = route && route.key === routeKey ? route.drives : null

  const offerTell = (item: CalendarItem) => {
    if (!isStop(item)) return
    void readStop(item).then((card) => { if (card.contact) setTell(card) }).catch(() => {})
  }

  /** Letting go, or **Done**: written as the desktop writes a drag, or put back with the reason it cannot go there. */
  const drop = (next: PhoneLift) => {
    const item = next.item
    if (!item) return
    const plan = liftPlan(item, next.from, next.to, next)
    if (!plan) { setLift(null); return }
    const why = dropRefusal(item, next.from, next.to)
    if (why) { setLift(null); setNotice(why); return }
    const held: PhoneLift = { ...next, mode: 'held' }
    setLift(held)
    /* What the server said about where it was put, while it moved. */
    const key = routeKeyOf(next)
    const runsInto = key && route?.key === key ? route.runsInto : null
    const sameDay = next.to.date === next.from.date && next.to.personId === next.from.personId
    const write = (repeat: 'one' | 'all' | null) => {
      setLift({ ...held, mode: 'saving' })
      writeMove(item, plan, repeat, zone)
        .then(() => {
          reload()
          /* Every one is a change to the series, not a stop running into the next: nothing to undo here. */
          if (sameDay && runsInto && repeat !== 'all') {
            const back = { date: next.from.date, start: item.start, minutes: null, personId: null }
            setInto({ item, words: runsInto, undo: () => writeMove(item, back, repeat, zone) })
          } else {
            offerTell(item)
          }
        })
        .catch((cause: unknown) => {
          setLift(null)
          setNotice(sentenceOf(cause, plan.verb === 'resize' ? `${item.title} couldn’t be changed.` : `${item.title} couldn’t be moved.`))
          reload()
        })
    }
    /* A repeat asks first, held where it was dropped until it is answered (Aaron, Oct 24, small Q3). */
    if (asksWhich(item)) {
      setAsking({
        item, verb: plan.verb, to: { date: plan.date, start: plan.start, minutes: next.end - next.start },
        onPick: (which) => { setAsking(null); write(which) },
        onCancel: () => { setAsking(null); setLift(null) },
      })
      return
    }
    write(null)
  }

  const finish = () => {
    if (!lift || lift.mode !== 'edit') return
    if (lift.item) { drop(lift); return }
    setLift(null)
    onNext({ personId: lift.to.personId, date: lift.to.date, start: lift.start, minutes: lift.end - lift.start })
  }
  const cancel = () => setLift(null)
  const keep = () => {
    if (!into) return
    setInto(null)
    offerTell(into.item)
  }
  const undo = async () => {
    if (!into) return
    const was = into
    setInto(null)
    try {
      await was.undo()
    } catch (cause) {
      setNotice(sentenceOf(cause, `${was.item.title} couldn’t be put back.`))
    }
    reload()
  }

  const overlays = (
    <>
      {lift?.mode === 'edit' && (
        <>
          {/* Edit mode is somewhere to have gone: back puts it back, as Cancel does (Aaron, Oct 26). */}
          <BackLayer onBack={cancel} />
          <EditBar lift={lift} title={titleOf(lift.to.personId)} onCancel={cancel} onDone={finish} />
        </>
      )}
      {asking && (
        <WhichSheet title={asking.item.title} verb={asking.verb} date={asking.item.date}
          lines={whichLines(asking.item, asking.to)} every={asking.item.kind === 'block' ? 'Every week' : undefined}
          onPick={asking.onPick} onClose={asking.onCancel} />
      )}
      {into && <RunsIntoPrompt words={into.words} onKeep={keep} onUndo={() => void undo()} />}
      {tell && !lift && !into && (
        <TellPrompt card={tell} canSend={canSend} touch onClose={() => setTell(null)}
          onMessage={(channel) => { setTell(null); messageAbout(tell, channel, true) }}
          onConfirm={() => { setTell(null); if (tell.job) openNewRequest({ book: { jobId: tell.job.id, appointmentId: tell.id } }) }} />
      )}
      {notice && (
        <div role="status"
          className="fixed left-1/2 z-[80] max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-[10px] border border-line bg-surface px-4 py-2.5 text-[13px] font-medium leading-[normal] text-text shadow-popover"
          style={{ bottom: lift?.mode === 'edit' ? 'calc(80px + var(--activity-foot, 0px))' : 'calc(24px + var(--activity-foot, 0px))' }}>
          {notice}
        </div>
      )}
    </>
  )

  return { lift, setLift, drop, drives, overlays }
}

/**
 * *… runs into Fox Hollow at 12:30. Keep it here?* — the desktop asks it under
 * the stop (§6.5); on a phone it stands at the foot, **Keep** and **Undo** a
 * finger's 44 px.
 */
function RunsIntoPrompt({ words, onKeep, onUndo }: { words: string; onKeep: () => void; onUndo: () => void }) {
  return (
    <div role="alertdialog" aria-label={words} data-overlay-layer={46}
      className="fixed left-1/2 z-[80] flex w-[calc(100vw-24px)] -translate-x-1/2 flex-col gap-2.5 rounded-[12px] border border-danger-ring bg-surface px-4 py-3 text-[13px] leading-[normal] shadow-popover"
      style={{ bottom: 'calc(24px + var(--activity-foot, 0px))' }}>
      <span className="text-danger">{words}</span>
      <span className="flex justify-end gap-2">
        <button type="button" onClick={onKeep}
          className="inline-flex h-11 items-center rounded-lg border border-line bg-surface px-4 text-[14px] font-semibold text-text">Keep</button>
        <button type="button" onClick={onUndo}
          className="inline-flex h-11 items-center rounded-lg border border-line bg-surface px-4 text-[14px] font-semibold text-text">Undo</button>
      </span>
    </div>
  )
}
